import { ModelsPanel, modelsPanelStore } from '@openlayers/models/ui/ModelsPanel';
import { TileLayersPanelComponent } from '@openlayers/tile-layers/ui/TileLayersPanelComponent';
import { ImagesEditor } from '@pages/map/actions/ImagesEditor';
import { MapDocumentsEditor } from '@pages/map/actions/MapDocumentsEditor';
import { mapRootStore, mapStateFacade, mapStation, mapTools, mapUI, mapWrapper, massMap } from '@pages/map/map';
import { LogpointsPanel2dMapComponent } from '@pages/map/ui/logpoints/LogpointsPanel2dMapComponent';
import { LogpointsToolbar } from '@shared/logpoints/ui/LogpointsToolbar';
import { MassAdvancedEditor } from '@pages/map/ui/mass/MassAdvancedEditor';
import { MassGridEditor } from '@pages/map/ui/mass/MassGridEditor';
import { MassPanelList } from '@pages/mass/MassPanelList';
import { SavedViewAttachmentsService } from '@shared/attachments/SavedViewAttachmentsService';
import { DocumentsImagesPanelGenericComponent } from '@shared/documents-images/ui/DocumentsImagesPanelGenericComponent';
import { ExternalDocumentCreateEditDialog } from '@shared/documents/ui/ExternalDocumentCreateEditDialog';
import { PdfTemplateFill } from '@shared/documents/ui/PdfTemplateFill';
import { Events, EventsGlobal } from '@shared/Events';
import { FileFolderDownloadDialog } from '@shared/files/FileFolderDownloadDialog';
import { ImagesService } from '@shared/images/ImagesService';
import { FileTreeService } from '@shared/services/FileTreeService';
import { Drawer } from '@shared/ui-native/Drawer';
import { Button } from '@shared/ui-react/Button';
import { Catch } from '@shared/ui-react/Catch';
import { DialogUtils } from '@shared/ui-react/Dialog';
import { Dropdown } from '@shared/ui-react/Dropdown';
import { WindowManager } from '@shared/ui-react/FloatingWindow';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { BottomNav } from 'design-system';
import { ActiveAlignmentDropdown } from '@openlayers/alignments/ui/ActiveAlignmentDropdown';
import { SavedViewsPanelComponent } from '@shared/user-state/ui/SavedViewsPanelComponent';
import { cs } from '@shared/utils/utils-classname';
import { VehiclesPanelGeneric } from '@shared/vehicles/VehiclesPanelGeneric';
import { action, makeObservable, observable } from 'mobx';
import { Observer, observer } from 'mobx-react';
import * as React from 'react';
import { Notify } from '@shared/ui-native/Notify';
import { LogpointsEditor2dMap } from '@pages/map/actions/LogpointsEditor2dMap';
import { LogpointsDownloadDialog } from '@shared/logpoints/ui/LogpointsDownloadDialog';
import { Telemetry, TelemetryEventType, TelemetrySource } from '@shared/Telemetry';

type AllPanels =
    | VehiclesPanelGeneric
    | ModelsPanel
    | LogpointsPanel2dMapComponent
    | DocumentsImagesPanelGenericComponent
    | MassPanelList
    | SavedViewsPanelComponent
    | TileLayersPanelComponent;

@observer
export class Map2dSidebar extends React.Component {
    public activePanel: AllPanels = null;
    @observable public activePanelId: IMapPanelName = null;
    public activePanelIdBeforeCLose: IMapPanelName = null;

    public tabs = [
        this.hasVehicles ? { id: 'vehicles', icon: 'iki iki-equipment', label: __('menu.vehicles') } : null,
        { id: 'models', icon: 'iki iki-designs', label: __('menu.designs') },
        { id: 'documents', icon: 'iki iki-files', label: __('general.documents') },
        this.hasLogpoints ? { id: 'logpoints', icon: 'iki iki-asbuilt', label: __('menu.logpoints') } : null,
        this.hasPhotos ? { id: 'images', icon: 'iki iki-photos', label: __('general.images') } : null,
        this.hasMass ? { id: 'mass', icon: 'iki iki-cross-section', label: __('js_map.tools.measurements') } : null,
        this.hasSavedViews ? { id: 'savedViews', icon: 'iki iki-saved-views', label: __('menu.saved_views') } : null,
        { id: 'tileLayers', icon: 'iki iki-map-layers', label: __('menu.map_tile_layers') },
        //{ id: 'schedule', icon: 'iki iki-map-layers', label: __('Schedule') }
    ].filter(Boolean);

    public panels: {
        vehicles?: VehiclesPanelGeneric;
        models?: ModelsPanel;
        logpoints?: LogpointsPanel2dMapComponent;
        documents?: DocumentsImagesPanelGenericComponent;
        images?: DocumentsImagesPanelGenericComponent;
        mass?: MassPanelList;
        savedViews?: SavedViewsPanelComponent;
        tileLayers?: TileLayersPanelComponent;
        //schedule?: SchedulePanelList;
    } = null;

    private modelsPanelRef = (el: HTMLElement | null) => {
        if (el && !this.panels.models) {
            this.panels.models = new ModelsPanel(el, mapRootStore.getTreeData(), mapWrapper.featureLayers, mapWrapper.backgroundMaps, modelsPanelStore);
        } else if (!el) {
            this.panels.models = null;
        }
    };

    constructor(props) {
        super(props);
        makeObservable(this);

        this.panels = {};

        Events.on(EventsGlobal.MAP_OPEN_IMAGE_FOLDER_WITH_THUMBNAILS, (evt, { folderId, imageId }) => {
            this.activatePanelById('images');
            this.panels.images.openThumbnailsPanel(folderId, imageId);
        });

        Events.on(EventsGlobal.MAP_OPEN_DOCUMENTS_FOLDER_WITH_THUMBNAILS, (evt, { folderId, imageId }) => {
            this.activatePanelById('documents');
            this.panels.documents.openThumbnailsPanel(folderId, imageId);
        });

        Events.on('map-edit-image', (evt, image) => ImagesEditor.open([image], mapRootStore.imagesStore));
        Events.on('map-edit-document', (evt, document) =>
            mapRootStore.executeWithTreeData(treeData => {
                MapDocumentsEditor.openDrawer([document], mapRootStore.documentsStore, treeData, mapWrapper, mapTools);
            })
        );
    }

    componentDidMount() {
        Events.on(EventsGlobal.FLOATING_WINDOW_OPENED, (evt, data) => {
            if (data.isMobile) {
                this.closePanel();
            }
        });

        Events.on(EventsGlobal.FLOATING_WINDOW_CLOSED, (evt, data) => {
            if (data.isMobile && this.activePanelIdBeforeCLose) {
                this.activePanelId = this.activePanelIdBeforeCLose;
            }
        });
    }

    renderPanels = (): React.ReactNode => {
        if (mapRootStore.isInitializing) {
            return null;
        }

        const active = this.activePanelId;

        // Sections are stacked absolutely inside a relative container.
        // CSS opacity+transform transitions replace framer-motion for tab-switch animation.
        const panelStyle = (id: IMapPanelName): React.CSSProperties => ({
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            transition: 'opacity 0.1s ease-out, transform 0.1s ease-out',
            opacity: active === id ? 1 : 0,
            transform: active === id ? 'translateX(0)' : 'translateX(-8px)',
            pointerEvents: active === id ? 'auto' : 'none',
        });

        return (
            <>
                <div
                    className='sidebar-panel__mobile-close'
                    style={active ? {} : { display: 'none' }}
                    onClick={e => {
                        e.stopPropagation();
                        this.closePanel();
                    }}
                >
                    <i className='iki iki-chevron-left'></i>
                </div>

                {/* VEHICLES */}
                {this.hasVehicles && (
                    <section style={panelStyle('vehicles')}>
                        <header className='panel-title with-tabs'>{__('menu.vehicles')}</header>
                        <div className='sidebar-toolbar flex-row'>
                            <Button
                                secondary
                                subtle
                                icon='iki iki-fit-to-screen'
                                id='vehicles-panel-fit-visible-btn'
                                style={{ marginLeft: 'auto' }}
                                tooltip={__('js_map.fit_visible')}
                                onClick={() => {
                                    mapWrapper.fitVehiclesLayer();
                                    Telemetry.add(TelemetryEventType.MAP_EQUIPMENT_FIT_VIEW, TelemetrySource.MAP_2D_DESKTOP);
                                }}
                            />
                            {kuura.hasAisIntegrationEnabled() && (
                                <Dropdown closeOnBodyClick={false} buttonStyle={{ paddingLeft: 10, paddingRight: 10 }} className='ik-button-secondary--subtle'>
                                    <i className='iki iki-more' />
                                    <div className='no-select'>
                                        <div className='dropdown-list-title'>{__('general.settings')}:</div>
                                        <ul className='dropdown-list clickable'>
                                            <li onClick={mapRootStore.marineStore.toggleShow}>
                                                <input type='checkbox' checked={mapRootStore.marineStore.showOnMap} readOnly />
                                                {__('js_map.show_marine_vessels_on_map')}
                                            </li>
                                        </ul>
                                    </div>
                                </Dropdown>
                            )}
                        </div>

                        <VehiclesPanelGeneric
                            ref={el => (this.panels.vehicles = el)}
                            treeData={mapRootStore.treeData}
                            store={mapRootStore.vehiclesStore}
                            sidebarWidth={mapRootStore.sidebarWidth}
                            readOnly={false}
                            onFitVisible={null}
                            onPanToVehicle={v => mapWrapper.featureLayers.vehicles.panToVehicle(v.id)}
                            onLoadModels={uuids => this.panels.models.checkUuids(uuids)}
                        />
                    </section>
                )}

                {/* MODELS */}
                <section
                    id='map-2d-models-panel-wrapper'
                    style={panelStyle('models')}
                    ref={this.modelsPanelRef}
                >
                    <header className='panel-title'>{__('menu.designs')}</header>
                    <div id='js-app__models-panel__toolbar-wrapper' className='sidebar-toolbar'></div>
                    {/* Flex column with overflow hidden: toolbar rows keep natural height,
                        only the tree's internal scroll container overflows. minHeight: 0
                        lets the wrapper shrink below its content height inside the section. */}
                    <div id='js-app__models-panel__tree-wrapper' className='flex-column of-h flex-1' style={{ minHeight: 0 }}></div>
                </section>

                {/* LOGPOINTS */}
                {this.hasLogpoints && (
                    <section style={panelStyle('logpoints')}>
                        <Catch>
                            <header className='panel-title'>{__('menu.logpoints')}</header>
                            <nav className='sidebar-toolbar flex-row'>
                                <LogpointsToolbar
                                    mapWrapper={mapWrapper}
                                    onDrawLinecode={() => mapUI.editLogpointsLinecode()}
                                    onBulkEdit={() => LogpointsEditor2dMap.open(null, false, 'area', mapRootStore.treeData)}
                                    onDownload={() => {
                                        const logpointIds = mapWrapper.featureLayers.logpoints.layerSource.getFeatures().map(f => f.get('data').id);
                                        LogpointsDownloadDialog.open(logpointIds);
                                    }}
                                />
                            </nav>
                            <div className='sidebar-section-body'>
                                <LogpointsPanel2dMapComponent
                                    ref={el => (this.panels.logpoints = el)}
                                    logpointsLayer={mapWrapper.featureLayers.logpoints}
                                    treeData={mapRootStore.treeData}
                                    mobileView={mapRootStore.mobileView}
                                    sidebarWidth={mapRootStore.sidebarWidth}
                                />
                            </div>
                        </Catch>
                    </section>
                )}

                {/* DOCUMENTS */}
                <section id='map-2d-documents-panel-wrapper' style={panelStyle('documents')}>
                    <header className='panel-title'>{__('general.documents')}</header>
                    <DocumentsImagesPanelGenericComponent
                        ref={el => (this.panels.documents = el)}
                        store={mapRootStore.documentsStore}
                        storageKey='documents-images-panel'
                        type='documents'
                        mobileView={mapRootStore.mobileView}
                        treeData={mapRootStore.treeData}
                        pinLeftOffset={mapRootStore.sidebarWidth}
                        toggleExpandPanel={this.toggleExpandPanel}
                        onOpenItem={id => {
                            const thumbs = mapRootStore.documentsStore.thumbnails;
                            let queue = thumbs.filtered;
                            if (!queue.find(i => i.id === id)) {
                                // if user chose to show unfiltered thumbnails and clicked on thumbnail, we need to use unfiltered queue
                                queue = thumbs.unfiltered;
                            }
                            mapWrapper.featureLayers.documents.openDocumentById(
                                id,
                                queue.map(i => i.id)
                            );
                        }}
                        onGoToFile={uuid => {
                            mapUI.mapPanels.panels.images.showInPanel(uuid);
                        }}
                        onEditItem={id => {
                            const item = mapRootStore.documentsStore.getById(id);
                            MapDocumentsEditor.openDrawer([item], mapRootStore.documentsStore, mapRootStore.treeData, mapWrapper, mapTools);
                        }}
                        onEditItems={(items, activateEditLocation) =>
                            MapDocumentsEditor.openDrawer(items as IDocument[], mapRootStore.documentsStore, mapRootStore.treeData, mapWrapper, mapTools, activateEditLocation)
                        }
                        onCreateExternalDocument={this.openCreateExternalDocument}
                        onOpenFillTemplate={this.openFillTemplate}
                    />
                </section>

                {/* PHOTOS */}
                {this.hasPhotos && (
                    <section id='map-2d-images-panel-wrapper' style={panelStyle('images')}>
                        <header className='panel-title'>{__('menu.photos')}</header>
                        <DocumentsImagesPanelGenericComponent
                            ref={el => (this.panels.images = el)}
                            store={mapRootStore.imagesStore}
                            storageKey='documents-images-panel'
                            type='images'
                            treeData={mapRootStore.treeData}
                            mobileView={mapRootStore.mobileView}
                            pinLeftOffset={mapRootStore.sidebarWidth}
                            toggleExpandPanel={this.toggleExpandPanel}
                            onOpenItem={id => {
                                const thumbs = mapRootStore.imagesStore.thumbnails;
                                let queue = thumbs.filtered;
                                if (!queue.find(i => i.id === id)) {
                                    // if user chose to show unfiltered thumbnails and clicked on thumbnail, we need to use unfiltered queue
                                    queue = thumbs.unfiltered;
                                }
                                mapWrapper.featureLayers.images.openImageById(
                                    id,
                                    queue.map(i => i.id)
                                );
                            }}
                            onGoToFile={uuid => {
                                mapUI.mapPanels.panels.images.showInPanel(uuid);
                            }}
                            onEditItem={id => ImagesEditor.openForId(id, mapRootStore.imagesStore)}
                            onEditItems={(items, activateEditLocation) => ImagesEditor.open(items as IImage[], mapRootStore.imagesStore, activateEditLocation)}
                            onOpenUploader={() => mapUI.openImageUploader()}
                            onBulkEdit={() => ImagesEditor.open([], mapRootStore.imagesStore)}
                            onCreateExternalDocument={null}
                        />
                    </section>
                )}

                {/* MASS */}
                {this.hasMass && (
                    <section id='map-2d-mass-panel-wrapper' style={panelStyle('mass')}>
                        <header className='panel-title'>{__('js_map.tools.measurements')}</header>
                        <MassPanelList ref={el => (this.panels.mass = el)} mapWrapper={mapWrapper} mapTools={mapTools} treeData={mapRootStore.treeData} />
                    </section>
                )}

                {/* SAVED VIEWS */}
                {this.hasSavedViews && (
                    <section style={panelStyle('savedViews')}>
                        <SavedViewsPanelComponent
                            ref={el => (this.panels.savedViews = el)}
                            pageStateFacade={mapStateFacade}
                            treeData={mapRootStore.treeData}
                            pinLeftOffset={mapRootStore.sidebarWidth}
                            hasPartialLoadDialog={true}
                            canShowMarkersOnMap={true}
                            toggleExpandPanel={this.toggleExpandPanel}
                            onDownload={this.onDownloadSavedView}
                        />
                    </section>
                )}

                {/* TILE LAYERS */}
                <section id='map-2d-tile-panel-wrapper' style={panelStyle('tileLayers')}>
                    <header className='panel-title with-tabs'>{__('menu.map_tile_layers')}</header>
                    <TileLayersPanelComponent
                        mapWrapper={mapWrapper}
                        ref={el => (this.panels.tileLayers = el)}
                        treeData={mapRootStore.treeData}
                        tileLayers={mapWrapper.tileLayers}
                    />
                </section>

                {/* SCHEDULE */}
                {/*<section>*/}
                {/*    <header className="panel-title with-tabs">{__('Schedule')}</header>*/}
                {/*    <SchedulePanelList ref={el => this.panels.schedule = el}/>*/}
                {/*</section>*/}
            </>
        );
    };

    render() {
        const alignmentBar = (extraStyle: React.CSSProperties) => (
            <div
                style={{
                    padding: 'var(--ik-space-s) var(--ik-space-m)',
                    borderTop: '1px solid var(--ik-color-border-default)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--ik-space-s)',
                    flexShrink: 0,
                    background: 'var(--ik-color-surface-default)',
                    ...extraStyle,
                }}
            >
                <span
                    style={{
                        font: 'var(--ik-font-default-s)',
                        color: 'var(--ik-color-text-weakest)',
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                    }}
                >
                    Active line:
                </span>
                <div className='sidebar-alignment-dropdown' style={{ flex: 1, overflow: 'hidden', borderRadius: 'var(--ik-shape-br-xl)' }}>
                    <Catch>
                        <Observer>
                            {() => (
                                <ActiveAlignmentDropdown
                                    filetreeData={mapRootStore.treeData}
                                    selectedModelId={mapRootStore.activeAlignment.modelId}
                                    selectedIndex={mapRootStore.activeAlignment.index}
                                    onChange={mapRootStore.setActiveAlignment}
                                    storageKey='Map2d_activeAlignmentSelectTree'
                                    alignmentStore={mapRootStore.alignmentStore}
                                />
                            )}
                        </Observer>
                    </Catch>
                </div>
                <Observer>
                    {() => (
                        <>
                            <Button
                                secondary
                                small
                                icon='iki iki-map-pin'
                                tooltip={__('js_visualization.jump_to_station')}
                                onClick={() => mapUI?.panToStation()}
                                disabled={mapRootStore.activeAlignment?.modelId < 0}
                                style={{ borderRadius: 'var(--ik-shape-br-xl)' }}
                            />
                            <Button
                                secondary
                                small
                                icon='iki iki-fit-to-screen'
                                tooltip={__('js_map.fit_into_view')}
                                onClick={() => mapUI?.fitActiveAlignment()}
                                disabled={mapRootStore.activeAlignment?.modelId < 0}
                                style={{ borderRadius: 'var(--ik-shape-br-xl)' }}
                            />
                        </>
                    )}
                </Observer>
            </div>
        );

        return (
            <>
                <div
                    className={cs('sidebar-panel js-sidebar ol-left-padding', {
                        mobile: mapRootStore.mobileView,
                        fixed: mapRootStore.mobileView,
                    })}
                >
                    {mapRootStore.isInitializing && <section className='overlay-loading'></section>}

                    <BottomNav
                        items={this.tabs.map(tab => ({
                            id: tab.id,
                            icon: <i className={tab.icon} aria-hidden='true' />,
                            label: tab.label,
                        }))}
                        active={this.activePanelId}
                        onSelect={id => this.activatePanelById(id as any)}
                        footer={undefined}
                        panelWidth={mapRootStore.sidebarWidth ? mapRootStore.sidebarWidth - 52 : undefined}
                        panel={
                            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <div className='sidebar-panel__panels' style={{ flex: 1, minHeight: 0 }}>
                                    {this.renderPanels()}
                                    {mapRootStore.mobileView && alignmentBar({ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 10 })}
                                </div>
                                {!mapRootStore.mobileView && alignmentBar({ position: 'relative' })}
                            </div>
                        }
                    />
                </div>
            </>
        );
    }

    public toggleExpandPanel = () => {
        const holder = document.querySelector('#map-sidebar-holder') as HTMLDivElement | null;
        if (!holder) return;

        if (holder.offsetWidth < 600) {
            holder.style.width = '850px';
        } else {
            holder.style.width = '420px';
        }
    };

    public activatePanelById = action((id: IMapPanelName): void => {
        if (!id) return;

        if (id === 'bgMaps') id = 'models';

        this.activePanelId = id;
        this.activePanel = this.panels[id];

        if (mapRootStore.mobileView) {
            WindowManager.closeAll();
        }

        setTimeout(() => {
            const searchInput = document.querySelector('.sidebar-panel__panels .search-input') as HTMLInputElement;
            searchInput?.focus();
        }, 200);
    });

    public getActivePanelId = (): IMapPanelName => {
        return this.activePanelId || 'models';
    };

    openCreateExternalDocument = () => {
        let folderUuid = kuura.getRootFolderUuid();
        if (mapRootStore.documentsStore.showThumbnailsForFolderId) {
            const node = FileTreeService.findNodeById(mapRootStore.treeData, mapRootStore.documentsStore.showThumbnailsForFolderId, true);
            if (node) {
                folderUuid = node.uuid;
            }
        }
        const drawerElement = Drawer.open();
        Events.trigger('drawer-open.editor');

        ReactUtils.safeUnmountComponentAtNode(drawerElement);

        ReactUtils.safeRender(
            <>
                <header>
                    <div className='padded'>
                        <h2 style={{ marginBottom: 0 }}>{__('js_files.new_external_document')}</h2>
                    </div>
                </header>
                <ExternalDocumentCreateEditDialog
                    treeData={mapRootStore.treeData}
                    document={{
                        folderUuid,
                    }}
                    mapWrapper={mapWrapper}
                    mapTools={mapTools}
                    activateEditLocation={false}
                    onSuccess={() => {
                        mapRootStore.documentsStore.fetchAndSet();
                        Drawer.close();
                    }}
                    onClose={Drawer.close}
                />
            </>,
            drawerElement
        );
    };

    openFillTemplate = () => {
        const getRenamedName = (template: string, description: string, folderUuid: string, latLon: [number, number], doc: IDocument) => {
            const modelSelected = mapUI.mapPanels.panels.models.tree.getSelected();
            const modelName = modelSelected && !modelSelected.isFolder() ? modelSelected.ajaxData?.name : null;
            const folderName = FileTreeService.findNodeByUuid(mapRootStore.treeData, folderUuid ?? doc.folder?.uuid)?.name ?? null;
            description = description ?? doc.description;
            const alignment = {
                alignmentName: FileTreeService.findNodeById(mapRootStore.treeData, mapRootStore.activeAlignment?.modelId)?.name ?? null,
                station: mapUI.stationAndModelInfoBox?.stationInfo?.station,
            };
            const bValue = null; // no geolocation on desktop

            return ImagesService.renameImage(template, doc.name, folderName, description, new Date(doc.timestamp), latLon, modelName, bValue, alignment, null);
        };
        const Body = observer(() => (
            <PdfTemplateFill
                onSaved={document => {
                    console.log('onSaved document:', document);
                    mapRootStore.documentsStore.fetchAndSet().then(() => {
                        mapUI.mapPanels.panels.documents.tree.getByUuid(document.folder?.uuid).setChecked(1);
                    });
                    //mapRootStore.documentsStore.items.push(document);
                }}
                onClose={DialogUtils.close}
                getRenamedName={getRenamedName}
                latLon={mapStation.markerLatLon}
                openSavedPdfInFolder={uuid => {
                    this.activatePanelById('documents');
                    const item = mapRootStore.documentsStore.getByUuid(uuid);
                    if (item) {
                        this.panels.documents.openThumbnailsPanel(item.folderId, item.id);
                    } else {
                        Notify.warn('Cant find PDF');
                    }
                }}
                openSavedPdfOnMap={uuid => {
                    const item = mapRootStore.documentsStore.getByUuid(uuid);
                    if (item) {
                        mapWrapper.featureLayers.documents.openDocumentById(item.id);
                    } else {
                        Notify.warn('Cant find PDF');
                    }
                }}
            />
        ));

        WindowManager.open({
            title: __('js_files.fill_template'),
            id: 'template_fill',
            group: 'template_fill',
            showCloseButton: true,
            resize: 'both',
            defaultSizing: {
                width: Math.min(window.innerWidth * 0.5, 1200),
                height: Math.min(window.innerHeight * 0.8, 1200),
            },
            body: <Body />,
        });
    };

    focusFeatureInTree = (id: number) => {
        this.activatePanelById('models');
        this.panels.models.focusItem(id);
    };

    openInFilesPage = (id: number) => {
        this.panels.models.openInFilesPage(id);
    };

    downloadModel = (id: number) => {
        window.open(`/kuura/api/1/file/${id}/get`);
    };

    closePanel = () => {
        this.activePanelIdBeforeCLose = this.activePanelId;
        this.activePanel = null;
        this.activePanelId = null;
    };

    onDownloadSavedView = async (state: IPageState) => {
        let attachIds = [];
        let images = [];
        let documents = [];
        let estimatedSizeBytes = 0;

        try {
            const imagesState = (state as IMapPageState).data?.panel?.images;
            if (imagesState) {
                images = mapRootStore.imagesStore.getFiltered({
                    showOnMap: true,
                    type: imagesState.type as any,
                    folderIds: imagesState.checkedFolderIds,
                    text: imagesState.nameAndDescription,
                    userIds: imagesState.userIds,
                    fromTimestamp: imagesState.timestamp.from,
                    toTimestamp: imagesState.timestamp.to,
                });
                console.log('1111 images[0]:', images[0]);
                images.forEach(i => {
                    estimatedSizeBytes += i.filesize ?? i.size ?? 0;
                });
            }

            const documentsState = (state as IMapPageState).data?.panel?.documents;
            if (documentsState) {
                documents = mapRootStore.documentsStore.getFiltered({
                    showOnMap: true,
                    type: documentsState.type as any,
                    folderIds: documentsState.checkedFolderIds,
                    text: documentsState.nameAndDescription,
                    userIds: documentsState.userIds,
                    fromTimestamp: documentsState.timestamp.from,
                    toTimestamp: documentsState.timestamp.to,
                });
                documents.forEach(i => {
                    estimatedSizeBytes += i.filesize ?? i.size ?? 0;
                });
            }

            attachIds = await SavedViewAttachmentsService.getAttachments(state.uuid).then(list => list.attachments.map(a => a.documentId));
        } catch (e) {
            console.log('saved view onDownload error', e);
        }

        const files = [
            ...(state as IMapPageState).data.panel.models.fileIds,
            ...((state as IMapPageState).data.panel.bgMaps?.fileIds ?? []),
            ...(documents || []).map(i => i.id),
            ...(attachIds || []),
        ];

        const treeData = await mapRootStore.getTreeData();
        files.forEach(id => {
            const node = FileTreeService.findNodeById(treeData, id, false, true);
            if (node) {
                estimatedSizeBytes += node.filesize ?? node.size ?? 0;
            }
        });

        FileFolderDownloadDialog.open({
            files,
            imageIds: [...(images || []).map(i => i.id)],
            unpackedSizeBytes: estimatedSizeBytes,
        });
    };

    get hasVehicles() {
        return kuura.hasPrivilege('VEHICLE_PAGE');
    }

    get hasLogpoints() {
        return kuura.hasPrivilege('LOGPOINTS_PAGE');
    }

    get hasPhotos() {
        return kuura.hasPrivilege('IMAGES_R') || kuura.hasPrivilege('IMAGES_W');
    }

    get hasMass() {
        return kuura.hasPrivilege('MASS_PAGE');
    }

    get hasSavedViews() {
        return kuura.hasPrivilege('SAVED_VIEWS');
    }
}
